import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ScrollAnimation from '../components/ScrollAnimation';
import { services, aiProcessSteps } from '../data/mock';
import { Link } from 'react-router-dom';
import { 
  Bot, 
  MessageSquare, 
  Eye, 
  FileText, 
  Settings,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const iconMap = {
  Bot,
  MessageSquare,
  Eye,
  FileText,
  Settings
};

const Services = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <ScrollAnimation>
            <h1 className="text-5xl md:text-7xl font-league-spartan font-extralight text-gray-900 mb-6 tracking-tight leading-none">
              Our Services
            </h1>
          </ScrollAnimation>
          <ScrollAnimation delay={200}>
            <p className="text-xl text-gray-600 font-league-spartan font-light mb-12 max-w-3xl mx-auto leading-relaxed">
              Comprehensive AI solutions tailored to your industry needs. From custom development to ready-to-deploy systems.
            </p>
          </ScrollAnimation>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <ScrollAnimation 
                  key={service.id} 
                  delay={index * 150}
                  className="h-full"
                >
                  <Card className="h-full bg-white border border-gray-200 hover:border-primary/20 transition-all duration-300 hover:shadow-xl group overflow-hidden">
                    <CardContent className="p-8 h-full flex flex-col">
                      <div className="flex items-start gap-6">
                        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                          <Icon className="h-8 w-8 text-primary group-hover:text-white transition-colors duration-300" />
                        </div>
                        <div className="flex-grow">
                          <h3 className="text-2xl font-league-spartan font-medium text-gray-900 mb-4 leading-tight">
                            {service.title}
                          </h3>
                          <p className="text-gray-600 font-league-spartan mb-6 leading-relaxed">
                            {service.description}
                          </p>
                          <p className="text-gray-700 font-league-spartan mb-8 leading-relaxed">
                            {service.details}
                          </p>
                          
                          {/* Key Features */}
                          <div className="space-y-3 mb-8">
                            <h4 className="font-league-spartan font-medium text-gray-900 mb-3">Key Benefits:</h4>
                            {getServiceBenefits(service.id).map((benefit, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                <span className="text-gray-600 font-league-spartan text-sm">{benefit}</span>
                              </div>
                            ))}
                          </div>

                          <Button asChild>
                            <Link to="/contact" className="flex items-center gap-2">
                              Get Started
                              <ArrowRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <ScrollAnimation className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Our AI Process
            </h2>
            <p className="text-lg text-gray-600 font-league-spartan leading-relaxed max-w-2xl mx-auto">
              A proven AI development lifecycle methodology to deliver exceptional solutions from strategy to optimization.
            </p>
          </ScrollAnimation>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {aiProcessSteps.map((step, index) => (
              <ScrollAnimation key={index} delay={index * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white font-league-spartan font-bold text-xl mx-auto mb-6">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-league-spartan font-medium text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 font-league-spartan leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollAnimation>
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 font-league-spartan mb-12 leading-relaxed">
              Let's discuss your AI needs and create a custom solution for your business.
            </p>
            <Button 
              asChild
              size="lg"
            >
              <Link to="/contact" className="flex items-center gap-2">
                Contact Us Today
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

// Helper function to get service benefits
const getServiceBenefits = (serviceId) => {
  const benefits = {
    1: [
      "Custom agents for any industry vertical",
      "Seamless integration with existing systems",
      "24/7 autonomous operation capabilities",
      "Scalable architecture for enterprise needs"
    ],
    2: [
      "Natural language understanding",
      "Multi-channel deployment support",
      "Advanced conversation management",
      "Real-time analytics and insights"
    ],
    3: [
      "High accuracy object detection",
      "Real-time image processing",
      "Custom model training available",
      "Edge deployment capabilities"
    ],
    4: [
      "Advanced text processing algorithms",
      "Multi-language support",
      "Custom domain adaptation",
      "API-first architecture"
    ],
    5: [
      "Tailored to your specific requirements",
      "End-to-end development support",
      "Production-ready deployment",
      "Ongoing maintenance and updates"
    ],
    6: [
      "Predict failures before they happen",
      "Minimize costly operational downtime",
      "Extend the lifespan of critical machinery",
      "Optimize maintenance schedules and costs"
    ],
    7: [
      "Achieve a comprehensive, human-like understanding",
      "Build more accurate and robust predictive models",
      "Unlock new insights by fusing different data types",
      "Enable next-generation, context-aware applications"
    ],
    8: [
      "Slash contract review time by up to 90%",
      "Proactively identify and mitigate contractual risks",
      "Gain a data-driven advantage in negotiations",
      "Unlock strategic insights from your contract portfolio"
    ]
    
  };
  return benefits[serviceId] || [];
};



export default Services;